import { logger } from './logger';
import { env } from './env';
import { withRetry } from '../utils/retry';
import { coachingContext } from './coaching-context';
import { modelConfig } from './model-config';

interface AIResponse {
  type: 'message' | 'error' | 'status' | 'plan_offer';
  content: string;
  planData?: {
    goal: string;
    actionItems: Array<{ id: string; text: string; status: 'pending' }>;
    timeline?: string;
  };
}

interface Message {
  role: 'user' | 'coach';
  content: string;
}

interface EdgeFunctionResponse {
  content: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  error?: string;
}

class RealtimeAIService {
  private static instance: RealtimeAIService;
  private listeners: ((response: AIResponse) => void)[] = [];
  private maxRetries = 3;
  private edgeFunctionUrl: string;

  private constructor() {
    this.edgeFunctionUrl = `${env.VITE_SUPABASE_URL}/functions/v1/ai-coach-chat`;
  }

  public static getInstance(): RealtimeAIService {
    if (!RealtimeAIService.instance) {
      RealtimeAIService.instance = new RealtimeAIService();
    }
    return RealtimeAIService.instance;
  }

  public async sendMessage(
    content: string,
    conversationHistory: Message[] = [],
    systemPrompt?: string,
    userId?: string,
    topicId?: string
  ): Promise<void> {
    try {
      this.notifyListeners({
        type: 'status',
        content: 'processing'
      });

      const defaultSystemPrompt = "You are a supportive life coach and accountability partner for men aged 16-40. You build genuine relationships by remembering details from past conversations and referencing them to show continuity. Your approach is motivational and encouraging, with a mix of gentle guidance and occasional directness when needed. You help them discover their own answers through powerful questions: 'What do you think would work best for you?' 'What's holding you back from taking that step?' Celebrate small wins and progress. Be honest but kind about challenges. Teach them to think critically and build independence - your goal is for them to eventually coach themselves. You're like a wise mentor who genuinely cares about their journey and believes in their potential. Balance support with accountability. Make conversations feel different from generic AI - be warm, personal, and remember their story. Keep responses conversational and under 150 words.\n\nCRITICAL CAREER GUIDANCE RULES:\n- Your mission is to help them explore NEW paths, not turn their current habits or hobbies into careers\n- If someone mentions gaming, streaming, social media, or similar interests: REDIRECT them to explore completely different fields\n- NEVER suggest careers like: streamer, content creator, game tester, pro gamer, YouTuber, influencer, or similar entertainment-focused paths\n- Instead, challenge them: 'I hear you like gaming, but let me ask - have you ever considered trades like electrical work, plumbing, or HVAC? What about hands-on careers where you build real things?'\n- Push them toward careers that require real-world skills, leaving the house, and building expertise in unfamiliar territory\n- When they express interest in careers related to their distractions or time-wasting habits, gently but firmly redirect: 'I want to challenge you to think bigger than that. What fields have you never explored?'\n- Prioritize suggesting: skilled trades, technical certifications, apprenticeships, healthcare, emergency services, construction, manufacturing, outdoor work, and other hands-on careers\n- Help them see beyond their bubble and discover opportunities they haven't considered\n\nIMPORTANT: When users express interest in careers, trades, apprenticeships, or skill development, our AI system will automatically search the web and curate real, current resources (apprenticeship programs, trade schools, certification sites, job boards, etc.) that appear below your message. You don't need to provide links or specific organizations - just focus on guidance, encouragement, and helping them think through their path. The resource recommendations are generated dynamically based on the conversation context and are pulled from live internet sources.\n\nACTION PLAN CREATION - THIS IS CRITICAL:\nYou MUST create action plans proactively when you detect goal-setting conversations. Here are the triggers:\n- User mentions wanting to change, improve, or work on something specific\n- User expresses interest in career paths, skill development, or personal growth\n- After 2-3 messages discussing a specific goal or challenge\n- When user asks 'what should I do' or 'how do I start' or similar action-oriented questions\n- When user shares they want accountability or a plan\n\nDO NOT ask 'would you like me to create a plan?' - Just CREATE IT. Be directive and confident.\n\nUse this EXACT format (the system parses this automatically):\n\n[ACTION_PLAN_START]\nGOAL: [One clear, specific, measurable goal statement]\nTIMELINE: [Realistic timeframe: '1 week', '2 weeks', '30 days', '3 months', etc.]\nSTEPS:\n1. [First concrete action - must be specific and immediate]\n2. [Second action - builds on first, involves research or exploration]\n3. [Third action - requires reaching out or real-world interaction]\n4. [Fourth action - practicing skills or gaining experience]\n5. [Fifth action - reflection or next-level commitment]\n[ACTION_PLAN_END]\n\nAfter the plan format, add 2-3 sentences of encouragement. Keep it brief.\n\nEXAMPLES OF GOOD PLANS:\n- Goal: 'Explore electrician career path' with steps like researching local programs, calling 3 trade schools, shadowing an electrician\n- Goal: 'Build confidence in social situations' with steps like saying hi to 3 strangers, joining one meetup, practicing eye contact\n- Goal: 'Stop wasting time on gaming' with steps like tracking screen time, replacing 1 hour with walks, finding one new hobby\n\nBE PROACTIVE. CREATE PLANS. MAKE THEM ACTIONABLE AND SPECIFIC.";

      let contextPrompt = '';
      if (userId) {
        try {
          contextPrompt = await coachingContext.buildContextPrompt(userId, topicId || undefined);
          await coachingContext.updateSessionCount(userId);
        } catch (error) {
          logger.error('Error building coaching context:', error);
        }
      }

      const messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }> = [
        {
          role: 'system',
          content: (systemPrompt || defaultSystemPrompt) + contextPrompt
        }
      ];

      conversationHistory.forEach(msg => {
        messages.push({
          role: msg.role === 'coach' ? 'assistant' : 'user',
          content: msg.content
        });
      });

      messages.push({
        role: 'user',
        content: content
      });

      const config = modelConfig.getConfig();

      const response = await withRetry<EdgeFunctionResponse>(
        async () => {
          const res = await fetch(this.edgeFunctionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              messages,
              model: config.name,
              temperature: 0.7,
              max_tokens: 300
            })
          });

          if (!res.ok) {
            const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(errorData.error || `HTTP ${res.status}`);
          }

          return res.json();
        },
        {
          maxAttempts: this.maxRetries,
          backoffMs: 1000,
          retryableErrors: ['timeout', 'ECONNABORTED', 'network', 'socket hang up', 'Connection error', 'fetch failed', '500', '502', '503', '504'],
          onRetry: (attempt, error) => {
            logger.warn(`AI request retry attempt ${attempt}/${this.maxRetries}:`, error.message);
            this.notifyListeners({
              type: 'status',
              content: `retrying (${attempt}/${this.maxRetries})`
            });
          }
        }
      );

      if (response.error) {
        throw new Error(response.error);
      }

      const aiResponse = response.content || "I'm here to help you take action. What are you working on or stuck with?";

      const planData = this.extractActionPlan(aiResponse);

      const words = aiResponse.split(' ');
      for (const word of words) {
        this.notifyListeners({
          type: 'message',
          content: word + ' '
        });
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      if (planData) {
        this.notifyListeners({
          type: 'plan_offer',
          content: aiResponse,
          planData
        });
      }

      this.notifyListeners({
        type: 'status',
        content: 'complete'
      });
    } catch (error: any) {
      logger.error('AI response error:', error);

      let errorMessage = 'Unable to connect to coaching service. Please try again.';
      if (error.message?.includes('API key') || error.message?.includes('authentication') || error.message?.includes('401')) {
        errorMessage = 'AI service is currently unavailable. Our team has been notified and is working on a fix.';
        logger.error('CRITICAL: AI authentication failed', { error: error.message });
      } else if (error.message?.includes('timeout') || error.message?.includes('ECONNABORTED')) {
        errorMessage = 'Request took too long. Please try again.';
      } else if (error.message?.includes('rate') || error.message?.includes('429')) {
        errorMessage = 'Service is experiencing high demand. Please wait a moment and try again.';
      } else if (error.message?.includes('Connection error') || error.message?.includes('fetch failed') || error.message?.includes('network')) {
        errorMessage = 'Unable to connect to coaching service. Please check your connection and try again.';
      }

      this.notifyListeners({
        type: 'error',
        content: errorMessage
      });
    }
  }

  public addListener(callback: (response: AIResponse) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  private notifyListeners(response: AIResponse) {
    this.listeners.forEach(listener => {
      try {
        listener(response);
      } catch (error) {
        logger.error('Error in listener:', error);
      }
    });
  }

  private extractActionPlan(response: string): { goal: string; actionItems: Array<{ id: string; text: string; status: 'pending' }>; timeline?: string } | null {
    try {
      const planMatch = response.match(/\[ACTION_PLAN_START\]([\s\S]*?)\[ACTION_PLAN_END\]/);
      if (!planMatch) return null;

      const planContent = planMatch[1];

      const goalMatch = planContent.match(/GOAL:\s*(.+?)(?:\n|$)/);
      const timelineMatch = planContent.match(/TIMELINE:\s*(.+?)(?:\n|$)/);
      const stepsMatch = planContent.match(/STEPS:\s*([\s\S]+?)(?=\[ACTION_PLAN_END\]|$)/);

      if (!goalMatch || !stepsMatch) return null;

      const goal = goalMatch[1].trim();
      const timeline = timelineMatch ? timelineMatch[1].trim() : undefined;

      const stepLines = stepsMatch[1]
        .split('\n')
        .map(line => line.trim())
        .filter(line => /^\d+\./.test(line));

      const actionItems = stepLines.map((step, index) => ({
        id: `${Date.now()}-${index}`,
        text: step.replace(/^\d+\.\s*/, ''),
        status: 'pending' as const
      }));

      if (actionItems.length === 0) return null;

      return { goal, actionItems, timeline };
    } catch (error) {
      logger.error('Error extracting action plan:', error);
      return null;
    }
  }
}

export const realtimeAI = RealtimeAIService.getInstance();
